import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { DSModal, DSButton, DSInput, DSIcon } from '../../design-system/primitives';
import { MembershipChip } from '../MembershipChip';
import '../../pages/membership/membership.css';

// "Which member is this?"
//
// Used by every queue action that needs a person: linking a Square customer,
// assigning a spare subscription to the member it covers, crediting a payment.
// Each of those is a matching interaction, not a form: the admin already knows
// who the answer is, so the only job here is making them findable fast.
//
// Searches the full account list rather than only members, because the whole
// point of most of these rows is that the person is not currently recognised as
// a member.

function MemberPicker({ title, description, excludeUserId, onPick, onClose }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiService.getUsers()
      .then((response) => { if (!cancelled) setUsers(response.data || []); })
      .catch(() => { if (!cancelled) setUsers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Without a search term this shows the first few accounts rather than all of
    // them: a list of 500 names is not a chooser, it is a wall.
    const pool = users.filter((u) => u.id !== excludeUserId);
    if (!term) return pool.slice(0, 8);

    return pool.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return name.includes(term)
        || (u.email || '').toLowerCase().includes(term)
        || (u.phone_number || '').includes(term);
    }).slice(0, 25);
  }, [users, search, excludeUserId]);

  return (
    <DSModal title={title} size="md" onClose={onClose} hideClose>
      {description && <p className="ds-modal-message">{description}</p>}

      <DSInput
        id="member-picker-search"
        type="search"
        placeholder="Search by name, email or phone"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        leadingIcon={<DSIcon name="search" />}
        autoFocus
      />

      <div className="member-picker__results">
        {loading && <p className="member-picker__empty">Loading...</p>}

        {!loading && matches.length === 0 && (
          <p className="member-picker__empty">
            {search ? 'Nobody matches that.' : 'No accounts to show.'}
          </p>
        )}

        {matches.map((user) => (
          <button
            key={user.id}
            type="button"
            className="member-picker__result"
            onClick={() => onPick(user)}
          >
            <span className="member-picker__name">
              {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
            </span>
            <span className="member-picker__email">{user.email}</span>
            <MembershipChip
              status={user.status}
              state={user.membership_state}
              size="sm"
              withIcon={false}
            />
          </button>
        ))}
      </div>

      <div className="ds-modal-actions">
        <DSButton appearance="outline" tone="neutral" onClick={onClose}>Cancel</DSButton>
      </div>
    </DSModal>
  );
}

export default MemberPicker;
